import { createContext } from 'preact';
import type { Size } from '#utils/UNSAFE_size';

/**
 * Props of the FormContext
 */
// TODO: investigate labelEdge, labelStartWidth, labelWrapping and see if they still need to be on the form components,
// or if we can just use a FormContext to pass this to a single component when it isn't in a form layout. See JET-51152
type FormContextProps = {
  /**
   * Indicates whether the form layout is disabled.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the component is inside a form layout.
   */
  isFormLayout?: boolean;
  /**
   * Indicates whether the form layout is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies the label position.
   */
  labelEdge?: 'inside' | 'start' | 'top';
  /**
   * Specifies the width of the start aligned label, ignored for top & inside aligned.
   */
  labelStartWidth?: Size;
  /**
   * Specifies if start or top label text should wrap or truncate.
   * @deprecated Since 18.0.0. Label truncation for 'start' and 'top' aligned labels is no longer recommended by the Redwood Design System. The default for labelWrapping was 'wrap' and that is now the only suggested pattern by UX design for 'start' and 'top' aligned labels. 'inside' aligned labels are always truncated per UX design and are not affected by this property's value.
   */
  labelWrapping?: 'truncate' | 'wrap';
  /**
   * Specifies the text alignment of the value.
   */
  textAlign?: 'start' | 'end' | 'right';
  /**
   * Specifies the density of the user assistance presentation.
   */
  userAssistanceDensity?: 'reflow' | 'efficient' | 'compact';
};

const DefaultFormContext: FormContextProps = {
  isDisabled: false,
  isFormLayout: false,
  isReadonly: false,
  labelEdge: 'inside',
  labelStartWidth: '33%',
  labelWrapping: 'wrap',
  // JET-72775 - Form control alignment in tables
  // The default value of the text align needs to be determined based on the
  // usage of the components, so we will use undefined as a default here and let
  // the consuming component handle the default value.
  textAlign: undefined,
  userAssistanceDensity: 'reflow'
};

/**
 * Context which the parent component can use to provide various FormLayout related
 * information to descendant form controls.
 */
const FormContext = createContext<FormContextProps>(DefaultFormContext);

export { DefaultFormContext, FormContext };
export type { FormContextProps };
